import * as R from "ramda";
import { isNilOrEmpty } from "utils";

export const toRegionOptions = R.map((c) => ({ label: c.Name, value: c.ISO }));
export const toStateOptions = R.map((c) => ({ label: c.Name, value: c.Name }));

export const countryHasStates = (countryIso) =>
  R.pipe(R.find(R.propEq("ISO", countryIso)), R.prop("States"), R.complement(R.isNil))(regions);

export const mapUpperName = R.map((e) => R.assoc("NAME", R.toUpper(R.prop("Name", e)), e));

export const getStatesForCountry = (countryISO) =>
  R.pipe(R.find(R.propEq("ISO", countryISO)), R.defaultTo({}), R.prop("States"))(regions);

export const isValidState = (country, state) => {
  if (isNilOrEmpty(country)) return true;

  const states = getStatesForCountry(country);
  // country has no states => true
  if (isNilOrEmpty(states)) return true;
  // country has states, no state passed => false
  if (isNilOrEmpty(state)) return false;

  // true on matche of ISO or name
  return R.either(
    R.any(R.propEq("ISO", R.toUpper(state))),
    R.any(R.propEq("NAME", R.toUpper(state)))
  )(states);
};

const getStateProp = (countryISO, stateStr, prop) => {
  if (isNilOrEmpty(countryISO) || isNilOrEmpty(stateStr)) return null;

  const states = getStatesForCountry(countryISO);
  if (isNilOrEmpty(states)) return null;

  const foundByISO = R.find(R.propEq("ISO", R.toUpper(stateStr)))(states);
  if (!isNilOrEmpty(foundByISO)) {
    return R.prop(prop, foundByISO);
  }
  const foundByName = R.find(R.propEq("NAME", R.toUpper(stateStr)))(states);
  if (!isNilOrEmpty(foundByName)) {
    return R.prop(prop, foundByName);
  }
  return null;
};

export const toStateISO = (countryISO, stateStr) => getStateProp(countryISO, stateStr, "ISO");
export const toStateName = (countryISO, stateStr) => getStateProp(countryISO, stateStr, "Name");

// ISOs in this list will float to the top of the country list, in the order specified
const pulled = ["US", "CA"];

const indexOf = (ISO) => R.findIndex(R.propEq("ISO", ISO));
const getEntry = (countries) => (iso) => countries[indexOf(iso)(countries)];
const getPulledEntries = (countries) => R.map(getEntry(countries))(pulled);

const notPulled = (c) => !R.includes(R.prop("ISO", c), pulled);
const sortRemainingEntries = R.pipe(R.filter(notPulled), R.sortBy(R.prop("Name")));

export const sortCountries = (countries) =>
  getPulledEntries(countries).concat(sortRemainingEntries(countries));

export const localizedRegionsFactory = (formatMessage) => {
  return [
    {
      Name: formatMessage({
        defaultMessage: "United States",
        description: "Country Name",
      }),
      ISO: "US",
      ALPHA3: "USA",
      PrimaryCurrency: "USD",
      States: mapUpperName([
        { Name: "Alaska", ISO: "AK" },
        { Name: "Alabama", ISO: "AL" },
        { Name: "Arkansas", ISO: "AR" },
        { Name: "Arizona", ISO: "AZ" },
        { Name: "California", ISO: "CA" },
        { Name: "Colorado", ISO: "CO" },
        { Name: "Connecticut", ISO: "CT" },
        { Name: "District of Columbia", ISO: "DC" },
        { Name: "Delaware", ISO: "DE" },
        { Name: "Florida", ISO: "FL" },
        { Name: "Georgia", ISO: "GA" },
        { Name: "Guam", ISO: "GU" },
        { Name: "Hawaii", ISO: "HI" },
        { Name: "Iowa", ISO: "IA" },
        { Name: "Idaho", ISO: "ID" },
        { Name: "Illinois", ISO: "IL" },
        { Name: "Indiana", ISO: "IN" },
        { Name: "Kansas", ISO: "KS" },
        { Name: "Kentucky", ISO: "KY" },
        { Name: "Louisiana", ISO: "LA" },
        { Name: "Massachusetts", ISO: "MA" },
        { Name: "Maryland", ISO: "MD" },
        { Name: "Maine", ISO: "ME" },
        { Name: "Michigan", ISO: "MI" },
        { Name: "Minnesota", ISO: "MN" },
        { Name: "Missouri", ISO: "MO" },
        { Name: "Mississippi", ISO: "MS" },
        { Name: "Montana", ISO: "MT" },
        { Name: "North Carolina", ISO: "NC" },
        { Name: "North Dakota", ISO: "ND" },
        { Name: "Nebraska", ISO: "NE" },
        { Name: "New Hampshire", ISO: "NH" },
        { Name: "New Jersey", ISO: "NJ" },
        { Name: "New Mexico", ISO: "NM" },
        { Name: "Nevada", ISO: "NV" },
        { Name: "New York", ISO: "NY" },
        { Name: "Ohio", ISO: "OH" },
        { Name: "Oklahoma", ISO: "OK" },
        { Name: "Oregon", ISO: "OR" },
        { Name: "Pennsylvania", ISO: "PA" },
        { Name: "Puerto Rico", ISO: "PR" },
        { Name: "Rhode Island", ISO: "RI" },
        { Name: "South Carolina", ISO: "SC" },
        { Name: "South Dakota", ISO: "SD" },
        { Name: "Tennessee", ISO: "TN" },
        { Name: "Texas", ISO: "TX" },
        { Name: "Utah", ISO: "UT" },
        { Name: "Virginia", ISO: "VA" },
        { Name: "Virgin Islands", ISO: "VI" },
        { Name: "Vermont", ISO: "VT" },
        { Name: "Washington", ISO: "WA" },
        { Name: "Wisconsin", ISO: "WI" },
        { Name: "West Virginia", ISO: "WV" },
        { Name: "Wyoming", ISO: "WY" },
        { Name: "Army Europe", ISO: "AE" },
        { Name: "Army Pacific", ISO: "AP" },
        { Name: "Army Americas", ISO: "AA" },
      ]),
    },
    {
      Name: formatMessage({
        defaultMessage: "Canada",
        description: "Country Name",
      }),
      ISO: "CA",
      ALPHA3: "CAN",
      PrimaryCurrency: "CAD",
      States: mapUpperName([
        { Name: "Alberta", ISO: "AB" },
        { Name: "British Columbia", ISO: "BC" },
        { Name: "Manitoba", ISO: "MB" },
        { Name: "New Brunswick", ISO: "NB" },
        { Name: "Newfoundland", ISO: "NL" },
        { Name: "Northwest Territories", ISO: "NT" },
        { Name: "Nova Scotia", ISO: "NS" },
        { Name: "Nunavut", ISO: "NU" },
        { Name: "Ontario", ISO: "ON" },
        { Name: "Prince Edward Island", ISO: "PE" },
        { Name: "Quebec", ISO: "QC" },
        { Name: "Saskatchewan", ISO: "SK" },
        { Name: "Yukon Territory", ISO: "YT" },
      ]),
    },
    {
      ISO: "AD",
      ALPHA3: "AND",
      Name: formatMessage({
        defaultMessage: "Andorra",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "AE",
      ALPHA3: "ARE",
      Name: formatMessage({
        defaultMessage: "United Arab Emirates",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AF",
      ALPHA3: "AFG",
      Name: formatMessage({
        defaultMessage: "Afghanistan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AI",
      ALPHA3: "AIA",
      Name: formatMessage({
        defaultMessage: "Anguilla",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AL",
      ALPHA3: "ALB",
      Name: formatMessage({
        defaultMessage: "Albania",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AM",
      ALPHA3: "ARM",
      Name: formatMessage({
        defaultMessage: "Armenia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AO",
      ALPHA3: "AGO",
      Name: formatMessage({
        defaultMessage: "Angola",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AQ",
      ALPHA3: "ATA",
      Name: formatMessage({
        defaultMessage: "Antarctica",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AR",
      ALPHA3: "ARG",
      Name: formatMessage({
        defaultMessage: "Argentina",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AS",
      ALPHA3: "ASM",
      Name: formatMessage({
        defaultMessage: "American Samoa",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AT",
      ALPHA3: "AUT",
      Name: formatMessage({
        defaultMessage: "Austria",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "AU",
      ALPHA3: "AUS",
      Name: formatMessage({
        defaultMessage: "Australia",
        description: "Country Name",
      }),
      PrimaryCurrency: "AUD",
    },
    {
      ISO: "AW",
      ALPHA3: "ABW",
      Name: formatMessage({
        defaultMessage: "Aruba",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AZ",
      ALPHA3: "AZE",
      Name: formatMessage({
        defaultMessage: "Azerbaidjan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BA",
      ALPHA3: "BIH",
      Name: formatMessage({
        defaultMessage: "Bosnia-Herzegovina",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BB",
      ALPHA3: "BRB",
      Name: formatMessage({
        defaultMessage: "Barbados",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BD",
      ALPHA3: "BGD",
      Name: formatMessage({
        defaultMessage: "Bangladesh",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BE",
      ALPHA3: "BEL",
      Name: formatMessage({
        defaultMessage: "Belgium",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "BF",
      ALPHA3: "BFA",
      Name: formatMessage({
        defaultMessage: "Burkina Faso",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "BG",
      ALPHA3: "BGR",
      Name: formatMessage({
        defaultMessage: "Bulgaria",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BH",
      ALPHA3: "BHR",
      Name: formatMessage({
        defaultMessage: "Bahrain",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BI",
      ALPHA3: "BDI",
      Name: formatMessage({
        defaultMessage: "Burundi",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BJ",
      ALPHA3: "BEN",
      Name: formatMessage({
        defaultMessage: "Benin",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "BM",
      ALPHA3: "BMU",
      Name: formatMessage({
        defaultMessage: "Bermuda",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BN",
      ALPHA3: "BRN",
      Name: formatMessage({
        defaultMessage: "Brunei Darussalam",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BO",
      ALPHA3: "BOL",
      Name: formatMessage({
        defaultMessage: "Bolivia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BQ",
      ALPHA3: "BES",
      Name: formatMessage({
        defaultMessage: "Bonaire",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BR",
      ALPHA3: "BRA",
      Name: formatMessage({
        defaultMessage: "Brazil",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BS",
      ALPHA3: "BHS",
      Name: formatMessage({
        defaultMessage: "Bahamas",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BT",
      ALPHA3: "BTN",
      Name: formatMessage({
        defaultMessage: "Bhutan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BV",
      ALPHA3: "BVT",
      Name: formatMessage({
        defaultMessage: "Bouvet Island",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BW",
      ALPHA3: "BWA",
      Name: formatMessage({
        defaultMessage: "Botswana",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BY",
      ALPHA3: "BLR",
      Name: formatMessage({
        defaultMessage: "Belarus",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "BZ",
      ALPHA3: "BLZ",
      Name: formatMessage({
        defaultMessage: "Belize",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CC",
      ALPHA3: "CCK",
      Name: formatMessage({
        defaultMessage: "Cocos Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CF",
      ALPHA3: "CAF",
      Name: formatMessage({
        defaultMessage: "Central African Republic",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CG",
      ALPHA3: "COG",
      Name: formatMessage({
        defaultMessage: "Congo",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "CH",
      ALPHA3: "CHE",
      Name: formatMessage({
        defaultMessage: "Switzerland",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CI",
      ALPHA3: "CIV",
      Name: formatMessage({
        defaultMessage: "Cote d'Ivoire",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "CK",
      ALPHA3: "COK",
      Name: formatMessage({
        defaultMessage: "Cook Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CL",
      ALPHA3: "CHL",
      Name: formatMessage({
        defaultMessage: "Chile",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CM",
      ALPHA3: "CMR",
      Name: formatMessage({
        defaultMessage: "Cameroon",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CN",
      ALPHA3: "CHN",
      Name: formatMessage({
        defaultMessage: "China",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CO",
      ALPHA3: "COL",
      Name: formatMessage({
        defaultMessage: "Colombia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CR",
      ALPHA3: "CRI",
      Name: formatMessage({
        defaultMessage: "Costa Rica",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CZ",
      ALPHA3: "CZE",
      Name: formatMessage({
        defaultMessage: "Czech Republic",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CV",
      ALPHA3: "CPV",
      Name: formatMessage({
        defaultMessage: "Cape Verde",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CX",
      ALPHA3: "CXR",
      Name: formatMessage({
        defaultMessage: "Christmas Island",
        description: "Country Name",
      }),
      PrimaryCurrency: "AUD",
    },
    {
      ISO: "CY",
      ALPHA3: "CYP",
      Name: formatMessage({
        defaultMessage: "Cyprus",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "DE",
      ALPHA3: "DEU",
      Name: formatMessage({
        defaultMessage: "Germany",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "DJ",
      ALPHA3: "DJI",
      Name: formatMessage({
        defaultMessage: "Djibouti",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "DK",
      ALPHA3: "DNK",
      Name: formatMessage({
        defaultMessage: "Denmark",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "DO",
      ALPHA3: "DOM",
      Name: formatMessage({
        defaultMessage: "Dominican Republic",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "DZ",
      ALPHA3: "DZA",
      Name: formatMessage({
        defaultMessage: "Algeria",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "EC",
      ALPHA3: "ECU",
      Name: formatMessage({
        defaultMessage: "Ecuador",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "EE",
      ALPHA3: "EST",
      Name: formatMessage({
        defaultMessage: "Estonia",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "EG",
      ALPHA3: "EGY",
      Name: formatMessage({
        defaultMessage: "Egypt",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "EH",
      ALPHA3: "ESH",
      Name: formatMessage({
        defaultMessage: "Western Sahara",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ES",
      ALPHA3: "ESP",
      Name: formatMessage({
        defaultMessage: "Spain",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "ET",
      ALPHA3: "ETH",
      Name: formatMessage({
        defaultMessage: "Ethiopia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "FI",
      ALPHA3: "FIN",
      Name: formatMessage({
        defaultMessage: "Finland",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "FJ",
      ALPHA3: "FJI",
      Name: formatMessage({
        defaultMessage: "Fiji",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "FK",
      ALPHA3: "FLK",
      Name: formatMessage({
        defaultMessage: "Falkland Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "FM",
      ALPHA3: "FSM",
      Name: formatMessage({
        defaultMessage: "Micronesia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "FO",
      ALPHA3: "FRO",
      Name: formatMessage({
        defaultMessage: "Faroe Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "FR",
      ALPHA3: "FRA",
      Name: formatMessage({
        defaultMessage: "France",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "GA",
      ALPHA3: "GAB",
      Name: formatMessage({
        defaultMessage: "Gabon",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "GD",
      ALPHA3: "GRD",
      Name: formatMessage({
        defaultMessage: "Grenada",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GE",
      ALPHA3: "GEO",
      Name: formatMessage({
        defaultMessage: "Georgia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GY",
      ALPHA3: "GUY",
      Name: formatMessage({
        defaultMessage: "Guyana",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GH",
      ALPHA3: "GHA",
      Name: formatMessage({
        defaultMessage: "Ghana",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GI",
      ALPHA3: "GIB",
      Name: formatMessage({
        defaultMessage: "Gibraltar",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GL",
      ALPHA3: "GRL",
      Name: formatMessage({
        defaultMessage: "Greenland",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GM",
      ALPHA3: "GMB",
      Name: formatMessage({
        defaultMessage: "Gambia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GN",
      ALPHA3: "GIN",
      Name: formatMessage({
        defaultMessage: "Guinea",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GP",
      ALPHA3: "GLP",
      Name: formatMessage({
        defaultMessage: "Guadeloupe",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "GQ",
      ALPHA3: "GNQ",
      Name: formatMessage({
        defaultMessage: "Equatorial Guinea",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GR",
      ALPHA3: "GRC",
      Name: formatMessage({
        defaultMessage: "Greece",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "GT",
      ALPHA3: "GTM",
      Name: formatMessage({
        defaultMessage: "Guatemala",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GU",
      ALPHA3: "GUM",
      Name: formatMessage({
        defaultMessage: "Guam",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GW",
      ALPHA3: "GNB",
      Name: formatMessage({
        defaultMessage: "Guinea Bissau",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "HK",
      ALPHA3: "HKG",
      Name: formatMessage({
        defaultMessage: "Hong Kong",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "HN",
      ALPHA3: "HND",
      Name: formatMessage({
        defaultMessage: "Honduras",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "HR",
      ALPHA3: "HRV",
      Name: formatMessage({
        defaultMessage: "Croatia",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "HT",
      ALPHA3: "HTI",
      Name: formatMessage({
        defaultMessage: "Haiti",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "HU",
      ALPHA3: "HUN",
      Name: formatMessage({
        defaultMessage: "Hungary",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ID",
      ALPHA3: "IDN",
      Name: formatMessage({
        defaultMessage: "Indonesia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "IE",
      ALPHA3: "IRL",
      Name: formatMessage({
        defaultMessage: "Ireland",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "IL",
      ALPHA3: "ISR",
      Name: formatMessage({
        defaultMessage: "Israel",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "IN",
      ALPHA3: "IND",
      Name: formatMessage({
        defaultMessage: "India",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "IQ",
      ALPHA3: "IRQ",
      Name: formatMessage({
        defaultMessage: "Iraq",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "IS",
      ALPHA3: "ISL",
      Name: formatMessage({
        defaultMessage: "Iceland",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "IT",
      ALPHA3: "ITA",
      Name: formatMessage({
        defaultMessage: "Italy",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "JM",
      ALPHA3: "JAM",
      Name: formatMessage({
        defaultMessage: "Jamaica",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "JO",
      ALPHA3: "JOR",
      Name: formatMessage({
        defaultMessage: "Jordan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "JP",
      ALPHA3: "JPN",
      Name: formatMessage({
        defaultMessage: "Japan",
        description: "Country Name",
      }),
      PrimaryCurrency: "JPY",
    },
    {
      ISO: "KE",
      ALPHA3: "KEN",
      Name: formatMessage({
        defaultMessage: "Kenya",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "KG",
      ALPHA3: "KGZ",
      Name: formatMessage({
        defaultMessage: "Kyrgyzstan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "KH",
      ALPHA3: "KHM",
      Name: formatMessage({
        defaultMessage: "Cambodia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "KI",
      ALPHA3: "KIR",
      Name: formatMessage({
        defaultMessage: "Kiribati",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "KM",
      ALPHA3: "COM",
      Name: formatMessage({
        defaultMessage: "Comoros",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "KR",
      ALPHA3: "KOR",
      Name: formatMessage({
        defaultMessage: "Korea South",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "KW",
      ALPHA3: "KWT",
      Name: formatMessage({
        defaultMessage: "Kuwait",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "KY",
      ALPHA3: "CYM",
      Name: formatMessage({
        defaultMessage: "Cayman Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "KZ",
      ALPHA3: "KAZ",
      Name: formatMessage({
        defaultMessage: "Kazakhstan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "LA",
      ALPHA3: "LAO",
      Name: formatMessage({
        defaultMessage: "Laos",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "LB",
      ALPHA3: "LBN",
      Name: formatMessage({
        defaultMessage: "Lebanon",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "LC",
      ALPHA3: "LCA",
      Name: formatMessage({
        defaultMessage: "Saint Lucia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "LI",
      ALPHA3: "LIE",
      Name: formatMessage({
        defaultMessage: "Liechtenstein",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "LK",
      ALPHA3: "LKA",
      Name: formatMessage({
        defaultMessage: "Sri Lanka",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "LR",
      ALPHA3: "LBR",
      Name: formatMessage({
        defaultMessage: "Liberia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "LS",
      ALPHA3: "LSO",
      Name: formatMessage({
        defaultMessage: "Lesotho",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "LT",
      ALPHA3: "LTU",
      Name: formatMessage({
        defaultMessage: "Lithuania",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "LU",
      ALPHA3: "LUX",
      Name: formatMessage({
        defaultMessage: "Luxembourg",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "LV",
      ALPHA3: "LVA",
      Name: formatMessage({
        defaultMessage: "Latvia",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "LY",
      ALPHA3: "LBY",
      Name: formatMessage({
        defaultMessage: "Libya",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "MA",
      ALPHA3: "MAR",
      Name: formatMessage({
        defaultMessage: "Morocco",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MC",
      ALPHA3: "MCO",
      Name: formatMessage({
        defaultMessage: "Monaco",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "MD",
      ALPHA3: "MDA",
      Name: formatMessage({
        defaultMessage: "Moldova",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MG",
      ALPHA3: "MDG",
      Name: formatMessage({
        defaultMessage: "Madagascar",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MH",
      ALPHA3: "MHL",
      Name: formatMessage({
        defaultMessage: "Marshall Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ML",
      ALPHA3: "MLI",
      Name: formatMessage({
        defaultMessage: "Mali",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "MM",
      ALPHA3: "MMR",
      Name: formatMessage({
        defaultMessage: "Myanmar",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MN",
      ALPHA3: "MNG",
      Name: formatMessage({
        defaultMessage: "Mongolia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MO",
      ALPHA3: "MAC",
      Name: formatMessage({
        defaultMessage: "Macau",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MP",
      ALPHA3: "MNP",
      Name: formatMessage({
        defaultMessage: "Northern Mariana Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MQ",
      ALPHA3: "MTQ",
      Name: formatMessage({
        defaultMessage: "Martinique",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "MR",
      ALPHA3: "MRT",
      Name: formatMessage({
        defaultMessage: "Mauritania",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MS",
      ALPHA3: "MSR",
      Name: formatMessage({
        defaultMessage: "Montserrat",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MT",
      ALPHA3: "MLT",
      Name: formatMessage({
        defaultMessage: "Malta",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "MU",
      ALPHA3: "MUS",
      Name: formatMessage({
        defaultMessage: "Mauritius",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MV",
      ALPHA3: "MDV",
      Name: formatMessage({
        defaultMessage: "Maldives",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MW",
      ALPHA3: "MWI",
      Name: formatMessage({
        defaultMessage: "Malawi",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MX",
      ALPHA3: "MEX",
      Name: formatMessage({
        defaultMessage: "Mexico",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MY",
      ALPHA3: "MYS",
      Name: formatMessage({
        defaultMessage: "Malaysia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MZ",
      ALPHA3: "MOZ",
      Name: formatMessage({
        defaultMessage: "Mozambique",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NA",
      ALPHA3: "NAM",
      Name: formatMessage({
        defaultMessage: "Namibia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NC",
      ALPHA3: "NCL",
      Name: formatMessage({
        defaultMessage: "New Caledonia",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "NE",
      ALPHA3: "NER",
      Name: formatMessage({
        defaultMessage: "Niger",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NF",
      ALPHA3: "NFK",
      Name: formatMessage({
        defaultMessage: "Norfolk Island",
        description: "Country Name",
      }),
      PrimaryCurrency: "AUD",
    },
    {
      ISO: "NG",
      ALPHA3: "NGA",
      Name: formatMessage({
        defaultMessage: "Nigeria",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NI",
      ALPHA3: "NIC",
      Name: formatMessage({
        defaultMessage: "Nicaragua",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NL",
      ALPHA3: "NLD",
      Name: formatMessage({
        defaultMessage: "Netherlands",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "NO",
      ALPHA3: "NOR",
      Name: formatMessage({
        defaultMessage: "Norway",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NP",
      ALPHA3: "NPL",
      Name: formatMessage({
        defaultMessage: "Nepal",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NR",
      ALPHA3: "NRU",
      Name: formatMessage({
        defaultMessage: "Nauru",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NU",
      ALPHA3: "NIU",
      Name: formatMessage({
        defaultMessage: "Niue",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "NZ",
      ALPHA3: "NZL",
      Name: formatMessage({
        defaultMessage: "New Zealand",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "OM",
      ALPHA3: "OMN",
      Name: formatMessage({
        defaultMessage: "Oman",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PA",
      ALPHA3: "PAN",
      Name: formatMessage({
        defaultMessage: "Panama",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PE",
      ALPHA3: "PER",
      Name: formatMessage({
        defaultMessage: "Peru",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PF",
      ALPHA3: "PYF",
      Name: formatMessage({
        defaultMessage: "French Polynesia",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "PG",
      ALPHA3: "PNG",
      Name: formatMessage({
        defaultMessage: "Papua New Guinea",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PH",
      ALPHA3: "PHL",
      Name: formatMessage({
        defaultMessage: "Philippines",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PK",
      ALPHA3: "PAK",
      Name: formatMessage({
        defaultMessage: "Pakistan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PL",
      ALPHA3: "POL",
      Name: formatMessage({
        defaultMessage: "Poland",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PM",
      ALPHA3: "SPM",
      Name: formatMessage({
        defaultMessage: "St. Pierre & Miquelon",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "PN",
      ALPHA3: "PCN",
      Name: formatMessage({
        defaultMessage: "Pitcairn",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PR",
      ALPHA3: "PRI",
      Name: formatMessage({
        defaultMessage: "Puerto Rico",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PT",
      ALPHA3: "PRT",
      Name: formatMessage({
        defaultMessage: "Portugal",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "PW",
      ALPHA3: "PLW",
      Name: formatMessage({
        defaultMessage: "Palau",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PY",
      ALPHA3: "PRY",
      Name: formatMessage({
        defaultMessage: "Paraguay",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "QA",
      ALPHA3: "QAT",
      Name: formatMessage({
        defaultMessage: "Qatar",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "RE",
      ALPHA3: "REU",
      Name: formatMessage({
        defaultMessage: "Reunion",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "RO",
      ALPHA3: "ROU",
      Name: formatMessage({
        defaultMessage: "Romania",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "RU",
      ALPHA3: "RUS",
      Name: formatMessage({
        defaultMessage: "Russia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "RW",
      ALPHA3: "RWA",
      Name: formatMessage({
        defaultMessage: "Rwanda",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SA",
      ALPHA3: "SAU",
      Name: formatMessage({
        defaultMessage: "Saudi Arabia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SB",
      ALPHA3: "SLB",
      Name: formatMessage({
        defaultMessage: "Solomon Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SC",
      ALPHA3: "SYC",
      Name: formatMessage({
        defaultMessage: "Seychelles",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SE",
      ALPHA3: "SWE",
      Name: formatMessage({
        defaultMessage: "Sweden",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SG",
      ALPHA3: "SGP",
      Name: formatMessage({
        defaultMessage: "Singapore",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SH",
      ALPHA3: "SHN",
      Name: formatMessage({
        defaultMessage: "St. Helena",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SI",
      ALPHA3: "SVN",
      Name: formatMessage({
        defaultMessage: "Slovenia",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "SJ",
      ALPHA3: "SJM",
      Name: formatMessage({
        defaultMessage: "Svalbard & Jan Mayen Island",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SK",
      ALPHA3: "SVK",
      Name: formatMessage({
        defaultMessage: "Slovakia",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "SL",
      ALPHA3: "SLE",
      Name: formatMessage({
        defaultMessage: "Sierra Leone",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SM",
      ALPHA3: "SMR",
      Name: formatMessage({
        defaultMessage: "San Marino",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "SN",
      ALPHA3: "SEN",
      Name: formatMessage({
        defaultMessage: "Senegal",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "SO",
      ALPHA3: "SOM",
      Name: formatMessage({
        defaultMessage: "Somalia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SR",
      ALPHA3: "SUR",
      Name: formatMessage({
        defaultMessage: "Suriname",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ST",
      ALPHA3: "STP",
      Name: formatMessage({
        defaultMessage: "Sao Tome and Principe",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SV",
      ALPHA3: "SLV",
      Name: formatMessage({
        defaultMessage: "El Salvador",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "SZ",
      ALPHA3: "SWZ",
      Name: formatMessage({
        defaultMessage: "Swaziland",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TC",
      ALPHA3: "TCA",
      Name: formatMessage({
        defaultMessage: "Turks & Caicos Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TD",
      ALPHA3: "TCD",
      Name: formatMessage({
        defaultMessage: "Chad",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "TF",
      ALPHA3: "ATF",
      Name: formatMessage({
        defaultMessage: "French Southern Terr.",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "TG",
      ALPHA3: "TGO",
      Name: formatMessage({
        defaultMessage: "Togo",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "TH",
      ALPHA3: "THA",
      Name: formatMessage({
        defaultMessage: "Thailand",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TJ",
      ALPHA3: "TJK",
      Name: formatMessage({
        defaultMessage: "Tajikistan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TK",
      ALPHA3: "TKL",
      Name: formatMessage({
        defaultMessage: "Tokelau",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TM",
      ALPHA3: "TKM",
      Name: formatMessage({
        defaultMessage: "Turkmenistan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TN",
      ALPHA3: "TUN",
      Name: formatMessage({
        defaultMessage: "Tunisia",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "TO",
      ALPHA3: "TON",
      Name: formatMessage({
        defaultMessage: "Tonga",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TL",
      ALPHA3: "TLS",
      Name: formatMessage({
        defaultMessage: "East Timor",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TR",
      ALPHA3: "TUR",
      Name: formatMessage({
        defaultMessage: "Turkey",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TT",
      ALPHA3: "TTO",
      Name: formatMessage({
        defaultMessage: "Trinidad & Tobago",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TV",
      ALPHA3: "TUV",
      Name: formatMessage({
        defaultMessage: "Tuvalu",
        description: "Country Name",
      }),
      PrimaryCurrency: "AUD",
    },
    {
      ISO: "TW",
      ALPHA3: "TWN",
      Name: formatMessage({
        defaultMessage: "Taiwan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "TZ",
      ALPHA3: "TZA",
      Name: formatMessage({
        defaultMessage: "Tanzania",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "UA",
      ALPHA3: "UKR",
      Name: formatMessage({
        defaultMessage: "Ukraine",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "UG",
      ALPHA3: "UGA",
      Name: formatMessage({
        defaultMessage: "Uganda",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GB",
      ALPHA3: "GBR",
      Name: formatMessage({
        defaultMessage: "United Kingdom",
        description: "Country Name",
      }),
      PrimaryCurrency: "GBP",
    },
    {
      ISO: "UY",
      ALPHA3: "URY",
      Name: formatMessage({
        defaultMessage: "Uruguay",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "UZ",
      ALPHA3: "UZB",
      Name: formatMessage({
        defaultMessage: "Uzbekistan",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "VA",
      ALPHA3: "VAT",
      Name: formatMessage({
        defaultMessage: "Vatican City State",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "VC",
      ALPHA3: "VCT",
      Name: formatMessage({
        defaultMessage: "St.Vincent & Grenadines",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "VE",
      ALPHA3: "VEN",
      Name: formatMessage({
        defaultMessage: "Venezuela",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "VN",
      ALPHA3: "VNM",
      Name: formatMessage({
        defaultMessage: "Vietnam",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "VU",
      ALPHA3: "VUT",
      Name: formatMessage({
        defaultMessage: "Vanuatu",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "WF",
      ALPHA3: "WLF",
      Name: formatMessage({
        defaultMessage: "Wallis and Futuna Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "WS",
      ALPHA3: "WSM",
      Name: formatMessage({
        defaultMessage: "Samoa",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "YE",
      ALPHA3: "YEM",
      Name: formatMessage({
        defaultMessage: "Yemen",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "YU",
      ALPHA3: "YUG",
      Name: formatMessage({
        defaultMessage: "Yugoslavia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ZA",
      ALPHA3: "ZAF",
      Name: formatMessage({
        defaultMessage: "South Africa",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ZM",
      ALPHA3: "ZMB",
      Name: formatMessage({
        defaultMessage: "Zambia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "CD",
      ALPHA3: "COD",
      Name: formatMessage({
        defaultMessage: "Democratic Republic of the Congo",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ZW",
      ALPHA3: "ZWE",
      Name: formatMessage({
        defaultMessage: "Zimbabwe",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "AG",
      ALPHA3: "ATG",
      Name: formatMessage({
        defaultMessage: "Antigua and Barbuda",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MK",
      ALPHA3: "MKD",
      Name: formatMessage({
        defaultMessage: "Macedonia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "RS",
      ALPHA3: "SRB",
      Name: formatMessage({
        defaultMessage: "Serbia",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ME",
      ALPHA3: "MNE",
      Name: formatMessage({
        defaultMessage: "Montenegro",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "AX",
      ALPHA3: "ALA",
      Name: formatMessage({
        defaultMessage: "Aland Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "BL",
      ALPHA3: "BLM",
      Name: formatMessage({
        defaultMessage: "Saint Bartelemey",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "DM",
      ALPHA3: "DMA",
      Name: formatMessage({
        defaultMessage: "Dominica",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "ER",
      ALPHA3: "ERI",
      Name: formatMessage({
        defaultMessage: "Eritrea",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "GF",
      ALPHA3: "GUF",
      Name: formatMessage({
        defaultMessage: "French Guiana",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
    {
      ISO: "GG",
      ALPHA3: "GGY",
      Name: formatMessage({
        defaultMessage: "Guernsey",
        description: "Country Name",
      }),
      PrimaryCurrency: "GBP",
    },
    {
      ISO: "GS",
      ALPHA3: "SGS",
      Name: formatMessage({
        defaultMessage: "South Georgia and the South Sandwich Islands",
        description: "Country Name",
      }),
      PrimaryCurrency: "GBP",
    },
    {
      ISO: "HM",
      ALPHA3: "HMD",
      Name: formatMessage({
        defaultMessage: "Heard Island and McDonald Islands",
        description: "Country Name",
      }),
      PrimaryCurrenc: "USD",
    },
    {
      ISO: "IM",
      ALPHA3: "IMN",
      Name: formatMessage({
        defaultMessage: "Isle of Man",
        description: "Country Name",
      }),
      PrimaryCurrency: "GBP",
    },
    {
      ISO: "IO",
      ALPHA3: "IOT",
      Name: formatMessage({
        defaultMessage: "British Indian Ocean Territory",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "JE",
      ALPHA3: "JEY",
      Name: formatMessage({
        defaultMessage: "Jersey",
        description: "Country Name",
      }),
      PrimaryCurrency: "GBP",
    },
    {
      ISO: "KN",
      ALPHA3: "KNA",
      Name: formatMessage({
        defaultMessage: "Saint Kitts and Nevis",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "MF",
      ALPHA3: "MAF",
      Name: formatMessage({
        defaultMessage: "Saint Martin",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "PS",
      ALPHA3: "PSE",
      Name: formatMessage({
        defaultMessage: "Palestinian Territory",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "UM",
      ALPHA3: "UMI",
      Name: formatMessage({
        defaultMessage: "United States Minor Outlying Islands",
        description: "Country Name",
      }),
      PrimaryCurrenc: "USD",
    },
    {
      ISO: "VG",
      ALPHA3: "VGB",
      Name: formatMessage({
        defaultMessage: "Virgin Islands, British",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "VI",
      ALPHA3: "VIR",
      Name: formatMessage({
        defaultMessage: "Virgin Islands, U.S.",
        description: "Country Name",
      }),
      PrimaryCurrency: "USD",
    },
    {
      ISO: "YT",
      ALPHA3: "MYT",
      Name: formatMessage({
        defaultMessage: "Mayotte",
        description: "Country Name",
      }),
      PrimaryCurrency: "EUR",
    },
  ];
};

export const regions = sortCountries([
  {
    Name: "United States",
    ISO: "US",
    PrimaryCurrency: "USD",
    States: mapUpperName([
      { Name: "Alaska", ISO: "AK" },
      { Name: "Alabama", ISO: "AL" },
      { Name: "Arkansas", ISO: "AR" },
      { Name: "Arizona", ISO: "AZ" },
      { Name: "California", ISO: "CA" },
      { Name: "Colorado", ISO: "CO" },
      { Name: "Connecticut", ISO: "CT" },
      { Name: "District of Columbia", ISO: "DC" },
      { Name: "Delaware", ISO: "DE" },
      { Name: "Florida", ISO: "FL" },
      { Name: "Georgia", ISO: "GA" },
      { Name: "Guam", ISO: "GU" },
      { Name: "Hawaii", ISO: "HI" },
      { Name: "Iowa", ISO: "IA" },
      { Name: "Idaho", ISO: "ID" },
      { Name: "Illinois", ISO: "IL" },
      { Name: "Indiana", ISO: "IN" },
      { Name: "Kansas", ISO: "KS" },
      { Name: "Kentucky", ISO: "KY" },
      { Name: "Louisiana", ISO: "LA" },
      { Name: "Massachusetts", ISO: "MA" },
      { Name: "Maryland", ISO: "MD" },
      { Name: "Maine", ISO: "ME" },
      { Name: "Michigan", ISO: "MI" },
      { Name: "Minnesota", ISO: "MN" },
      { Name: "Missouri", ISO: "MO" },
      { Name: "Mississippi", ISO: "MS" },
      { Name: "Montana", ISO: "MT" },
      { Name: "North Carolina", ISO: "NC" },
      { Name: "North Dakota", ISO: "ND" },
      { Name: "Nebraska", ISO: "NE" },
      { Name: "New Hampshire", ISO: "NH" },
      { Name: "New Jersey", ISO: "NJ" },
      { Name: "New Mexico", ISO: "NM" },
      { Name: "Nevada", ISO: "NV" },
      { Name: "New York", ISO: "NY" },
      { Name: "Ohio", ISO: "OH" },
      { Name: "Oklahoma", ISO: "OK" },
      { Name: "Oregon", ISO: "OR" },
      { Name: "Pennsylvania", ISO: "PA" },
      { Name: "Puerto Rico", ISO: "PR" },
      { Name: "Rhode Island", ISO: "RI" },
      { Name: "South Carolina", ISO: "SC" },
      { Name: "South Dakota", ISO: "SD" },
      { Name: "Tennessee", ISO: "TN" },
      { Name: "Texas", ISO: "TX" },
      { Name: "Utah", ISO: "UT" },
      { Name: "Virginia", ISO: "VA" },
      { Name: "Virgin Islands", ISO: "VI" },
      { Name: "Vermont", ISO: "VT" },
      { Name: "Washington", ISO: "WA" },
      { Name: "Wisconsin", ISO: "WI" },
      { Name: "West Virginia", ISO: "WV" },
      { Name: "Wyoming", ISO: "WY" },
      { Name: "Army Europe", ISO: "AE" },
      { Name: "Army Pacific", ISO: "AP" },
      { Name: "Army Americas", ISO: "AA" },
    ]),
  },
  {
    Name: "Canada",
    ISO: "CA",
    PrimaryCurrency: "CAD",
    States: mapUpperName([
      { Name: "Alberta", ISO: "AB" },
      { Name: "British Columbia", ISO: "BC" },
      { Name: "Manitoba", ISO: "MB" },
      { Name: "New Brunswick", ISO: "NB" },
      { Name: "Newfoundland", ISO: "NL" },
      { Name: "Northwest Territories", ISO: "NT" },
      { Name: "Nova Scotia", ISO: "NS" },
      { Name: "Nunavut", ISO: "NU" },
      { Name: "Ontario", ISO: "ON" },
      { Name: "Prince Edward Island", ISO: "PE" },
      { Name: "Quebec", ISO: "QC" },
      { Name: "Saskatchewan", ISO: "SK" },
      { Name: "Yukon Territory", ISO: "YT" },
    ]),
  },
  { ISO: "AD", Name: "Andorra", PrimaryCurrency: "EUR" },
  { ISO: "AE", Name: "United Arab Emirates", PrimaryCurrency: "USD" },
  { ISO: "AF", Name: "Afghanistan", PrimaryCurrency: "USD" },
  { ISO: "AI", Name: "Anguilla", PrimaryCurrency: "USD" },
  { ISO: "AL", Name: "Albania", PrimaryCurrency: "USD" },
  { ISO: "AM", Name: "Armenia", PrimaryCurrency: "USD" },
  { ISO: "AO", Name: "Angola", PrimaryCurrency: "USD" },
  { ISO: "AQ", Name: "Antarctica", PrimaryCurrency: "USD" },
  { ISO: "AR", Name: "Argentina", PrimaryCurrency: "USD" },
  { ISO: "AS", Name: "American Samoa", PrimaryCurrency: "USD" },
  { ISO: "AT", Name: "Austria", PrimaryCurrency: "EUR" },
  { ISO: "AU", Name: "Australia", PrimaryCurrency: "AUD" },
  { ISO: "AW", Name: "Aruba", PrimaryCurrency: "USD" },
  { ISO: "AZ", Name: "Azerbaidjan", PrimaryCurrency: "USD" },
  { ISO: "BA", Name: "Bosnia-Herzegovina", PrimaryCurrency: "USD" },
  { ISO: "BB", Name: "Barbados", PrimaryCurrency: "USD" },
  { ISO: "BD", Name: "Bangladesh", PrimaryCurrency: "USD" },
  { ISO: "BE", Name: "Belgium", PrimaryCurrency: "EUR" },
  { ISO: "BF", Name: "Burkina Faso", PrimaryCurrency: "EUR" },
  { ISO: "BG", Name: "Bulgaria", PrimaryCurrency: "USD" },
  { ISO: "BH", Name: "Bahrain", PrimaryCurrency: "USD" },
  { ISO: "BI", Name: "Burundi", PrimaryCurrency: "USD" },
  { ISO: "BJ", Name: "Benin", PrimaryCurrency: "EUR" },
  { ISO: "BM", Name: "Bermuda", PrimaryCurrency: "USD" },
  { ISO: "BN", Name: "Brunei Darussalam", PrimaryCurrency: "USD" },
  { ISO: "BO", Name: "Bolivia", PrimaryCurrency: "USD" },
  { ISO: "BQ", Name: "Bonaire", PrimaryCurrency: "USD" },
  { ISO: "BR", Name: "Brazil", PrimaryCurrency: "USD" },
  { ISO: "BS", Name: "Bahamas", PrimaryCurrency: "USD" },
  { ISO: "BT", Name: "Bhutan", PrimaryCurrency: "USD" },
  { ISO: "BV", Name: "Bouvet Island", PrimaryCurrency: "USD" },
  { ISO: "BW", Name: "Botswana", PrimaryCurrency: "USD" },
  { ISO: "BY", Name: "Belarus", PrimaryCurrency: "USD" },
  { ISO: "BZ", Name: "Belize", PrimaryCurrency: "USD" },
  { ISO: "CC", Name: "Cocos Islands", PrimaryCurrency: "USD" },
  { ISO: "CF", Name: "Central African Republic", PrimaryCurrency: "USD" },
  { ISO: "CG", Name: "Congo", PrimaryCurrency: "EUR" },
  { ISO: "CH", Name: "Switzerland", PrimaryCurrency: "USD" },
  { ISO: "CI", Name: "Cote d'Ivoire", PrimaryCurrency: "EUR" },
  { ISO: "CK", Name: "Cook Islands", PrimaryCurrency: "USD" },
  { ISO: "CL", Name: "Chile", PrimaryCurrency: "USD" },
  { ISO: "CM", Name: "Cameroon", PrimaryCurrency: "USD" },
  { ISO: "CN", Name: "China", PrimaryCurrency: "USD" },
  { ISO: "CO", Name: "Colombia", PrimaryCurrency: "USD" },
  { ISO: "CR", Name: "Costa Rica", PrimaryCurrency: "USD" },
  { ISO: "CZ", Name: "Czech Republic", PrimaryCurrency: "USD" },
  // { ISO: "CU", Name: "Cuba", PrimaryCurrency: "USD" },
  { ISO: "CV", Name: "Cape Verde", PrimaryCurrency: "USD" },
  { ISO: "CX", Name: "Christmas Island", PrimaryCurrency: "AUD" },
  { ISO: "CY", Name: "Cyprus", PrimaryCurrency: "EUR" },
  { ISO: "DE", Name: "Germany", PrimaryCurrency: "EUR" },
  { ISO: "DJ", Name: "Djibouti", PrimaryCurrency: "USD" },
  { ISO: "DK", Name: "Denmark", PrimaryCurrency: "USD" },
  { ISO: "DO", Name: "Dominican Republic", PrimaryCurrency: "USD" },
  { ISO: "DZ", Name: "Algeria", PrimaryCurrency: "USD" },
  { ISO: "EC", Name: "Ecuador", PrimaryCurrency: "USD" },
  { ISO: "EE", Name: "Estonia", PrimaryCurrency: "EUR" },
  { ISO: "EG", Name: "Egypt", PrimaryCurrency: "USD" },
  { ISO: "EH", Name: "Western Sahara", PrimaryCurrency: "USD" },
  { ISO: "ES", Name: "Spain", PrimaryCurrency: "EUR" },
  { ISO: "ET", Name: "Ethiopia", PrimaryCurrency: "USD" },
  { ISO: "FI", Name: "Finland", PrimaryCurrency: "EUR" },
  { ISO: "FJ", Name: "Fiji", PrimaryCurrency: "USD" },
  { ISO: "FK", Name: "Falkland Islands", PrimaryCurrency: "USD" },
  { ISO: "FM", Name: "Micronesia", PrimaryCurrency: "USD" },
  { ISO: "FO", Name: "Faroe Islands", PrimaryCurrency: "USD" },
  { ISO: "FR", Name: "France", PrimaryCurrency: "EUR" },
  { ISO: "GA", Name: "Gabon", PrimaryCurrency: "EUR" },
  { ISO: "GD", Name: "Grenada", PrimaryCurrency: "USD" },
  { ISO: "GE", Name: "Georgia", PrimaryCurrency: "USD" },
  { ISO: "GY", Name: "Guyana", PrimaryCurrency: "USD" },
  { ISO: "GH", Name: "Ghana", PrimaryCurrency: "USD" },
  { ISO: "GI", Name: "Gibraltar", PrimaryCurrency: "USD" },
  { ISO: "GL", Name: "Greenland", PrimaryCurrency: "USD" },
  { ISO: "GM", Name: "Gambia", PrimaryCurrency: "USD" },
  { ISO: "GN", Name: "Guinea", PrimaryCurrency: "USD" },
  { ISO: "GP", Name: "Guadeloupe", PrimaryCurrency: "EUR" },
  { ISO: "GQ", Name: "Equatorial Guinea", PrimaryCurrency: "USD" },
  { ISO: "GR", Name: "Greece", PrimaryCurrency: "EUR" },
  { ISO: "GT", Name: "Guatemala", PrimaryCurrency: "USD" },
  { ISO: "GU", Name: "Guam", PrimaryCurrency: "USD" },
  { ISO: "GW", Name: "Guinea Bissau", PrimaryCurrency: "USD" },
  { ISO: "HK", Name: "Hong Kong", PrimaryCurrency: "USD" },
  { ISO: "HN", Name: "Honduras", PrimaryCurrency: "USD" },
  { ISO: "HR", Name: "Croatia", PrimaryCurrency: "EUR" },
  { ISO: "HT", Name: "Haiti", PrimaryCurrency: "USD" },
  { ISO: "HU", Name: "Hungary", PrimaryCurrency: "USD" },
  { ISO: "ID", Name: "Indonesia", PrimaryCurrency: "USD" },
  { ISO: "IE", Name: "Ireland", PrimaryCurrency: "EUR" },
  { ISO: "IL", Name: "Israel", PrimaryCurrency: "USD" },
  { ISO: "IN", Name: "India", PrimaryCurrency: "USD" },
  { ISO: "IQ", Name: "Iraq", PrimaryCurrency: "USD" },
  // { ISO: "IR", Name: "Iran", PrimaryCurrency: "USD" },
  { ISO: "IS", Name: "Iceland", PrimaryCurrency: "USD" },
  { ISO: "IT", Name: "Italy", PrimaryCurrency: "EUR" },
  { ISO: "JM", Name: "Jamaica", PrimaryCurrency: "USD" },
  { ISO: "JO", Name: "Jordan", PrimaryCurrency: "USD" },
  { ISO: "JP", Name: "Japan", PrimaryCurrency: "JPY" },
  { ISO: "KE", Name: "Kenya", PrimaryCurrency: "USD" },
  { ISO: "KG", Name: "Kyrgyzstan", PrimaryCurrency: "USD" },
  { ISO: "KH", Name: "Cambodia", PrimaryCurrency: "USD" },
  { ISO: "KI", Name: "Kiribati", PrimaryCurrency: "USD" },
  { ISO: "KM", Name: "Comoros", PrimaryCurrency: "USD" },
  // { ISO: "KP", Name: "Korea North", PrimaryCurrency: "USD" },
  { ISO: "KR", Name: "Korea South", PrimaryCurrency: "USD" },
  { ISO: "KW", Name: "Kuwait", PrimaryCurrency: "USD" },
  { ISO: "KY", Name: "Cayman Islands", PrimaryCurrency: "USD" },
  { ISO: "KZ", Name: "Kazakhstan", PrimaryCurrency: "USD" },
  { ISO: "LA", Name: "Laos", PrimaryCurrency: "USD" },
  { ISO: "LB", Name: "Lebanon", PrimaryCurrency: "USD" },
  { ISO: "LC", Name: "Saint Lucia", PrimaryCurrency: "USD" },
  { ISO: "LI", Name: "Liechtenstein", PrimaryCurrency: "USD" },
  { ISO: "LK", Name: "Sri Lanka", PrimaryCurrency: "USD" },
  { ISO: "LR", Name: "Liberia", PrimaryCurrency: "USD" },
  { ISO: "LS", Name: "Lesotho", PrimaryCurrency: "USD" },
  { ISO: "LT", Name: "Lithuania", PrimaryCurrency: "EUR" },
  { ISO: "LU", Name: "Luxembourg", PrimaryCurrency: "EUR" },
  { ISO: "LV", Name: "Latvia", PrimaryCurrency: "EUR" },
  { ISO: "LY", Name: "Libya", PrimaryCurrency: "EUR" },
  { ISO: "MA", Name: "Morocco", PrimaryCurrency: "USD" },
  { ISO: "MC", Name: "Monaco", PrimaryCurrency: "EUR" },
  { ISO: "MD", Name: "Moldova", PrimaryCurrency: "USD" },
  { ISO: "MG", Name: "Madagascar", PrimaryCurrency: "USD" },
  { ISO: "MH", Name: "Marshall Islands", PrimaryCurrency: "USD" },
  { ISO: "ML", Name: "Mali", PrimaryCurrency: "EUR" },
  { ISO: "MM", Name: "Myanmar", PrimaryCurrency: "USD" },
  { ISO: "MN", Name: "Mongolia", PrimaryCurrency: "USD" },
  { ISO: "MO", Name: "Macau", PrimaryCurrency: "USD" },
  { ISO: "MP", Name: "Northern Mariana Islands", PrimaryCurrency: "USD" },
  { ISO: "MQ", Name: "Martinique", PrimaryCurrency: "EUR" },
  { ISO: "MR", Name: "Mauritania", PrimaryCurrency: "USD" },
  { ISO: "MS", Name: "Montserrat", PrimaryCurrency: "USD" },
  { ISO: "MT", Name: "Malta", PrimaryCurrency: "EUR" },
  { ISO: "MU", Name: "Mauritius", PrimaryCurrency: "USD" },
  { ISO: "MV", Name: "Maldives", PrimaryCurrency: "USD" },
  { ISO: "MW", Name: "Malawi", PrimaryCurrency: "USD" },
  { ISO: "MX", Name: "Mexico", PrimaryCurrency: "USD" },
  { ISO: "MY", Name: "Malaysia", PrimaryCurrency: "USD" },
  { ISO: "MZ", Name: "Mozambique", PrimaryCurrency: "USD" },
  { ISO: "NA", Name: "Namibia", PrimaryCurrency: "USD" },
  { ISO: "NC", Name: "New Caledonia", PrimaryCurrency: "EUR" },
  { ISO: "NE", Name: "Niger", PrimaryCurrency: "USD" },
  { ISO: "NF", Name: "Norfolk Island", PrimaryCurrency: "AUD" },
  { ISO: "NG", Name: "Nigeria", PrimaryCurrency: "USD" },
  { ISO: "NI", Name: "Nicaragua", PrimaryCurrency: "USD" },
  { ISO: "NL", Name: "Netherlands", PrimaryCurrency: "EUR" },
  { ISO: "NO", Name: "Norway", PrimaryCurrency: "USD" },
  { ISO: "NP", Name: "Nepal", PrimaryCurrency: "USD" },
  { ISO: "NR", Name: "Nauru", PrimaryCurrency: "USD" },
  //&Country{ISO:"NULL",Name:	"Neutral Zone"},
  { ISO: "NU", Name: "Niue", PrimaryCurrency: "USD" },
  { ISO: "NZ", Name: "New Zealand", PrimaryCurrency: "USD" },
  { ISO: "OM", Name: "Oman", PrimaryCurrency: "USD" },
  { ISO: "PA", Name: "Panama", PrimaryCurrency: "USD" },
  { ISO: "PE", Name: "Peru", PrimaryCurrency: "USD" },
  { ISO: "PF", Name: "French Polynesia", PrimaryCurrency: "EUR" },
  { ISO: "PG", Name: "Papua New Guinea", PrimaryCurrency: "USD" },
  { ISO: "PH", Name: "Philippines", PrimaryCurrency: "USD" },
  { ISO: "PK", Name: "Pakistan", PrimaryCurrency: "USD" },
  { ISO: "PL", Name: "Poland", PrimaryCurrency: "USD" },
  { ISO: "PM", Name: "St. Pierre & Miquelon", PrimaryCurrency: "EUR" },
  { ISO: "PN", Name: "Pitcairn", PrimaryCurrency: "USD" },
  { ISO: "PR", Name: "Puerto Rico", PrimaryCurrency: "USD" },
  { ISO: "PT", Name: "Portugal", PrimaryCurrency: "EUR" },
  { ISO: "PW", Name: "Palau", PrimaryCurrency: "USD" },
  { ISO: "PY", Name: "Paraguay", PrimaryCurrency: "USD" },
  { ISO: "QA", Name: "Qatar", PrimaryCurrency: "USD" },
  { ISO: "RE", Name: "Reunion", PrimaryCurrency: "EUR" },
  { ISO: "RO", Name: "Romania", PrimaryCurrency: "USD" },
  { ISO: "RU", Name: "Russia", PrimaryCurrency: "USD" },
  { ISO: "RW", Name: "Rwanda", PrimaryCurrency: "USD" },
  { ISO: "SA", Name: "Saudi Arabia", PrimaryCurrency: "USD" },
  { ISO: "SB", Name: "Solomon Islands", PrimaryCurrency: "USD" },
  { ISO: "SC", Name: "Seychelles", PrimaryCurrency: "USD" },
  // { ISO: "SD", Name: "Sudan", PrimaryCurrency: "USD" },
  { ISO: "SE", Name: "Sweden", PrimaryCurrency: "USD" },
  { ISO: "SG", Name: "Singapore", PrimaryCurrency: "USD" },
  { ISO: "SH", Name: "St. Helena", PrimaryCurrency: "USD" },
  { ISO: "SI", Name: "Slovenia", PrimaryCurrency: "EUR" },
  { ISO: "SJ", Name: "Svalbard & Jan Mayen Island", PrimaryCurrency: "USD" },
  { ISO: "SK", Name: "Slovakia", PrimaryCurrency: "EUR" },
  { ISO: "SL", Name: "Sierra Leone", PrimaryCurrency: "USD" },
  { ISO: "SM", Name: "San Marino", PrimaryCurrency: "EUR" },
  { ISO: "SN", Name: "Senegal", PrimaryCurrency: "EUR" },
  { ISO: "SO", Name: "Somalia", PrimaryCurrency: "USD" },
  { ISO: "SR", Name: "Suriname", PrimaryCurrency: "USD" },
  { ISO: "ST", Name: "Sao Tome and Principe", PrimaryCurrency: "USD" },
  //&Country{ISO:"NULL",Name:	"Soviet Union"},
  { ISO: "SV", Name: "El Salvador", PrimaryCurrency: "USD" },
  // { ISO: "SY", Name: "Syria", PrimaryCurrency: "USD" },
  { ISO: "SZ", Name: "Swaziland", PrimaryCurrency: "USD" },
  { ISO: "TC", Name: "Turks & Caicos Islands", PrimaryCurrency: "USD" },
  { ISO: "TD", Name: "Chad", PrimaryCurrency: "EUR" },
  { ISO: "TF", Name: "French Southern Terr.", PrimaryCurrency: "EUR" },
  { ISO: "TG", Name: "Togo", PrimaryCurrency: "EUR" },
  { ISO: "TH", Name: "Thailand", PrimaryCurrency: "USD" },
  { ISO: "TJ", Name: "Tajikistan", PrimaryCurrency: "USD" },
  { ISO: "TK", Name: "Tokelau", PrimaryCurrency: "USD" },
  { ISO: "TM", Name: "Turkmenistan", PrimaryCurrency: "USD" },
  { ISO: "TN", Name: "Tunisia", PrimaryCurrency: "EUR" },
  { ISO: "TO", Name: "Tonga", PrimaryCurrency: "USD" },
  { ISO: "TL", Name: "East Timor", PrimaryCurrency: "USD" },
  { ISO: "TR", Name: "Turkey", PrimaryCurrency: "USD" },
  { ISO: "TT", Name: "Trinidad & Tobago", PrimaryCurrency: "USD" },
  { ISO: "TV", Name: "Tuvalu", PrimaryCurrency: "AUD" },
  { ISO: "TW", Name: "Taiwan", PrimaryCurrency: "USD" },
  { ISO: "TZ", Name: "Tanzania", PrimaryCurrency: "USD" },
  { ISO: "UA", Name: "Ukraine", PrimaryCurrency: "USD" },
  { ISO: "UG", Name: "Uganda", PrimaryCurrency: "USD" },
  { ISO: "GB", Name: "United Kingdom", PrimaryCurrency: "GBP" },
  { ISO: "UY", Name: "Uruguay", PrimaryCurrency: "USD" },
  { ISO: "UZ", Name: "Uzbekistan", PrimaryCurrency: "USD" },
  { ISO: "VA", Name: "Vatican City State", PrimaryCurrency: "EUR" },
  { ISO: "VC", Name: "St.Vincent & Grenadines", PrimaryCurrency: "USD" },
  { ISO: "VE", Name: "Venezuela", PrimaryCurrency: "USD" },
  { ISO: "VN", Name: "Vietnam", PrimaryCurrency: "USD" },
  { ISO: "VU", Name: "Vanuatu", PrimaryCurrency: "USD" },
  { ISO: "WF", Name: "Wallis and Futuna Islands", PrimaryCurrency: "USD" },
  { ISO: "WS", Name: "Samoa", PrimaryCurrency: "USD" },
  { ISO: "YE", Name: "Yemen", PrimaryCurrency: "USD" },
  { ISO: "YU", Name: "Yugoslavia", PrimaryCurrency: "USD" },
  { ISO: "ZA", Name: "South Africa", PrimaryCurrency: "USD" },
  { ISO: "ZM", Name: "Zambia", PrimaryCurrency: "USD" },
  { ISO: "CD", Name: "Democratic Republic of the Congo", PrimaryCurrency: "USD" },
  { ISO: "ZW", Name: "Zimbabwe", PrimaryCurrency: "USD" },
  { ISO: "AG", Name: "Antigua and Barbuda", PrimaryCurrency: "USD" },
  { ISO: "MK", Name: "Macedonia", PrimaryCurrency: "USD" },
  { ISO: "RS", Name: "Serbia", PrimaryCurrency: "USD" },
  { ISO: "ME", Name: "Montenegro", PrimaryCurrency: "EUR" },
  { ISO: "AX", Name: "Aland Islands", PrimaryCurrency: "EUR" },
  { ISO: "BL", Name: "Saint Bartelemey", PrimaryCurrency: "USD" },
  { ISO: "DM", Name: "Dominica", PrimaryCurrency: "USD" },
  { ISO: "ER", Name: "Eritrea", PrimaryCurrency: "USD" },
  { ISO: "GF", Name: "French Guiana", PrimaryCurrency: "EUR" },
  { ISO: "GG", Name: "Guernsey", PrimaryCurrency: "GBP" },
  { ISO: "GS", Name: "South Georgia and the South Sandwich Islands", PrimaryCurrency: "GBP" },
  { ISO: "HM", Name: "Heard Island and McDonald Islands", PrimaryCurrency: "USD" },
  { ISO: "IM", Name: "Isle of Man", PrimaryCurrency: "GBP" },
  { ISO: "IO", Name: "British Indian Ocean Territory", PrimaryCurrency: "USD" },
  { ISO: "JE", Name: "Jersey", PrimaryCurrency: "GBP" },
  { ISO: "KN", Name: "Saint Kitts and Nevis", PrimaryCurrency: "USD" },
  { ISO: "MF", Name: "Saint Martin", PrimaryCurrency: "USD" },
  { ISO: "PS", Name: "Palestinian Territory", PrimaryCurrency: "USD" },
  { ISO: "UM", Name: "United States Minor Outlying Islands", PrimaryCurrency: "USD" },
  { ISO: "VG", Name: "Virgin Islands, British", PrimaryCurrency: "USD" },
  { ISO: "VI", Name: "Virgin Islands, U.S.", PrimaryCurrency: "USD" },
  { ISO: "YT", Name: "Mayotte", PrimaryCurrency: "EUR" },
]);

export const regionsAsOptions = toRegionOptions(regions);
export const isValidCountryIso = (code) => regions.some(R.propEq("ISO", code));